import { Controller } from "@hotwired/stimulus";

interface DropdownItem {
  label: string;
  url: string;
  current?: boolean;
  primary?: boolean;
  disabled?: boolean;
  tooltip?: string;
  modal?: "jquery" | "turbo";
  checkProxy?: boolean;
  method?: string;
  confirm?: string;
  divider?: boolean;
}

interface BackLink {
  label: string;
  url: string;
}

// The trigger button is server-rendered (see the ViewComponent's ERB); this
// controller reuses it and only builds the menu panel client-side, since —
// when `searchable` — it supports live text filtering, a "no results" empty
// state, and a current-item indicator (▶).
export default class extends Controller {
  static values = {
    items: Array,
    fallbackLabel: String,
    searchable: Boolean,
    searchPlaceholder: String,
    backLink: Object,
    truncateAt: Number,
    buttonTruncateAt: Number,
    excludeCurrentItem: Boolean,
    emptyStateText: String,
  };

  declare readonly itemsValue: DropdownItem[];
  declare readonly fallbackLabelValue: string;
  declare readonly searchableValue: boolean;
  declare readonly searchPlaceholderValue: string;
  declare readonly backLinkValue: Partial<BackLink>;
  declare readonly truncateAtValue: number;
  declare readonly buttonTruncateAtValue: number;
  declare readonly excludeCurrentItemValue: boolean;
  declare readonly emptyStateTextValue: string;

  private btn: HTMLButtonElement | null = null;
  private dropdown: HTMLElement | null = null;
  private outsideClickHandler: ((e: MouseEvent) => void) | null = null;
  private escapeHandler: ((e: KeyboardEvent) => void) | null = null;

  connect() {
    this.bindButton();
    this.buildDropdownIfAbsent();
    this.attachGlobalListeners();
  }

  // `data-click-bound` guards against double binding: connect() re-fires after a portal
  // move (see disconnect()) and would otherwise attach a second click listener.
  private bindButton() {
    const existingBtn = this.element.querySelector<HTMLButtonElement>(
      ":scope > .shared--searchable-dropdown-component__btn"
    );
    this.btn = existingBtn ?? this.buildButton();

    if (this.btn.dataset.clickBound === "true") return;
    this.btn.dataset.clickBound = "true";
    this.btn.addEventListener("click", () => this.toggle());
  }

  private buildDropdownIfAbsent() {
    const existingDropdown = this.element.querySelector<HTMLElement>(
      ":scope > .shared--searchable-dropdown-component__dropdown"
    );
    if (existingDropdown) {
      this.dropdown = existingDropdown;
      return;
    }

    this.buildDropdown();
  }

  private attachGlobalListeners() {
    this.outsideClickHandler = (e: MouseEvent) => {
      if (this.isOpen() && !this.element.contains(e.target as Node)) {
        this.close();
      }
    };
    document.addEventListener("click", this.outsideClickHandler);

    this.escapeHandler = (e: KeyboardEvent) => {
      if (e.key === "Escape" && this.isOpen()) this.close();
    };
    document.addEventListener("keydown", this.escapeHandler);
  }

  disconnect() {
    if (this.outsideClickHandler) {
      document.removeEventListener("click", this.outsideClickHandler);
      this.outsideClickHandler = null;
    }
    if (this.escapeHandler) {
      document.removeEventListener("keydown", this.escapeHandler);
      this.escapeHandler = null;
    }
    // Don't clear this.btn/this.dropdown: disconnect() also fires on a portal move (not
    // just teardown), and the nodes/flag move with this.element for connect() to re-discover.
  }

  private truncate(text: string, max: number): string {
    return text.length > max ? text.slice(0, max) + "…" : text;
  }

  private label(text: string, maxLength: number = this.truncateAtValue): string {
    return maxLength > 0 ? this.truncate(text, maxLength) : text;
  }

  // Fallback for callers that skip the server-rendered ERB button. Doesn't bind the
  // click listener — bindButton() does that for both paths.
  private buildButton(): HTMLButtonElement {
    const currentItem = this.itemsValue.find((i) => i.current);
    const buttonLabel = this.label(
      currentItem?.label ?? this.fallbackLabelValue,
      this.buttonTruncateAtValue
    );

    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "shared--searchable-dropdown-component__btn";
    btn.setAttribute("aria-haspopup", "true");
    btn.setAttribute("aria-expanded", "false");
    btn.innerHTML = `<span class="shared--searchable-dropdown-component__label">${buttonLabel}</span><span class="shared--searchable-dropdown-component__arrow" aria-hidden="true">&#9660;</span>`;
    this.element.appendChild(btn);
    return btn;
  }

  private buildDropdown() {
    // Pinned rows (back-link, search box) sit in their own <ul> above the scrollable item list.
    const dropdown = document.createElement("div");
    dropdown.className = "shared--searchable-dropdown-component__dropdown";
    dropdown.hidden = true;

    const pinnedList = document.createElement("ul");
    pinnedList.className = "shared--searchable-dropdown-component__pinned";

    const itemsList = document.createElement("ul");
    itemsList.className = "shared--searchable-dropdown-component__list";

    if (this.backLinkValue?.url) {
      const backLi = document.createElement("li");
      backLi.className = "shared--searchable-dropdown-component__back-link-item";
      const backA = document.createElement("a");
      backA.href = this.backLinkValue.url;
      backA.textContent = this.backLinkValue.label ?? "";
      backLi.appendChild(backA);
      pinnedList.appendChild(backLi);
    }

    let searchInput: HTMLInputElement | null = null;
    let emptyLi: HTMLElement | null = null;
    const searchableItems: { li: HTMLElement; searchText: string }[] = [];

    if (this.searchableValue) {
      const searchLi = document.createElement("li");
      searchLi.className = "shared--searchable-dropdown-component__search-item";
      searchInput = document.createElement("input");
      searchInput.type = "text";
      searchInput.className = "shared--searchable-dropdown-component__search";
      searchInput.placeholder = this.searchPlaceholderValue;
      searchInput.autocomplete = "off";
      searchLi.appendChild(searchInput);
      pinnedList.appendChild(searchLi);

      emptyLi = document.createElement("li");
      emptyLi.className = "shared--searchable-dropdown-component__search-empty";
      emptyLi.textContent = this.emptyStateTextValue || "No results found";
      emptyLi.hidden = true;
      itemsList.appendChild(emptyLi);
    }

    for (const item of this.itemsValue) {
      if (item.divider) {
        const dividerLi = document.createElement("li");
        dividerLi.className = "shared--searchable-dropdown-component__divider";
        dividerLi.setAttribute("role", "separator");
        dividerLi.setAttribute("aria-hidden", "true");
        dividerLi.textContent = "";
        // itemsList (not dropdown) — it's attached to dropdown after the loop, so
        // appending straight to dropdown put every divider above the whole list.
        itemsList.appendChild(dividerLi);
        continue;
      }

      if (item.current && this.excludeCurrentItemValue) continue;

      const li = document.createElement("li");

      if (item.current && !this.excludeCurrentItemValue) {
        const indicator = document.createElement("span");
        indicator.className = "shared--searchable-dropdown-component__current-indicator";
        indicator.setAttribute("aria-hidden", "true");
        indicator.textContent = "▶";
        li.appendChild(indicator);
      }

      if (item.disabled) {
        const span = document.createElement("span");
        span.className = "shared--searchable-dropdown-component__link--disabled";
        span.dataset.tooltip = item.tooltip ?? "";
        span.textContent = item.label;
        li.appendChild(span);
      } else {
        const a = document.createElement("a");
        a.href = item.url;
        const rawLabel = item.primary ? `Primary - ${item.label}` : item.label;
        a.textContent = this.label(rawLabel);
        a.title = rawLabel;
        if (item.primary) a.style.fontWeight = "bold";

        if (item.modal === "jquery") a.rel = "modal:open";
        if (item.modal === "turbo") {
          a.dataset.turbo = "true";
          a.dataset.turboFrame = "modal";
        }
        if (item.checkProxy) {
          a.addEventListener("click", (e) => {
            e.preventDefault();
            this.close();
            this.dispatch("checkProxy", { detail: { targetHref: item.url } });
          });
        } else {
          if (item.method) a.dataset.method = item.method;
          if (item.confirm) a.dataset.confirm = item.confirm;
        }

        li.appendChild(a);
      }

      itemsList.appendChild(li);

      if (this.searchableValue) {
        const rawLabel = item.primary ? `Primary - ${item.label}` : item.label;
        searchableItems.push({ li, searchText: rawLabel.toLowerCase() });
      }
    }

    if (searchInput && emptyLi) {
      searchInput.addEventListener("input", () => {
        const query = searchInput!.value.toLowerCase().trim();
        let visible = 0;
        searchableItems.forEach(({ li, searchText }) => {
          const match = !query || searchText.includes(query);
          li.hidden = !match;
          if (match) visible++;
        });
        emptyLi!.hidden = visible > 0;
        // Reset scroll on every keystroke, else a previous position can leave new matches out of view.
        itemsList.scrollTop = 0;
      });
    }

    this.dropdown = dropdown;

    if (pinnedList.childElementCount > 0) dropdown.appendChild(pinnedList);
    dropdown.appendChild(itemsList);

    this.element.appendChild(dropdown);
  }

  private isOpen(): boolean {
    return !!this.dropdown && !this.dropdown.hidden;
  }

  private toggle() {
    this.isOpen() ? this.close() : this.open();
  }

  private open() {
    if (!this.dropdown || !this.btn) return;
    this.dropdown.hidden = false;
    this.btn.setAttribute("aria-expanded", "true");

    if (!this.searchableValue) return;
    requestAnimationFrame(() => {
      if (this.dropdown && !this.dropdown.style.minWidth) {
        this.dropdown.style.minWidth = `${this.dropdown.offsetWidth}px`;
      }
      const search = this.dropdown?.querySelector<HTMLInputElement>(
        ".shared--searchable-dropdown-component__search"
      );
      search?.focus();
    });
  }

  private close() {
    if (!this.dropdown || !this.btn) return;
    this.dropdown.hidden = true;
    this.btn.setAttribute("aria-expanded", "false");
    if (!this.searchableValue) return;
    const search = this.dropdown.querySelector<HTMLInputElement>(
      ".shared--searchable-dropdown-component__search"
    );
    if (search) {
      search.value = "";
      search.dispatchEvent(new Event("input"));
    }
  }

}
